import React, { useEffect, useState, useCallback } from 'react';
import { Box, Container, Typography, Grid, Card, CardMedia, CardContent, TextField, Button, Switch, FormControlLabel, Snackbar, Alert, LinearProgress, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../context/AuthContext';
import ApiService from '../../services/api';

export default function AdsManagement() {
  const { showSnackbar } = useAuth();
  const [ads, setAds] = useState([]);
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [active, setActive] = useState(true);
  const [order, setOrder] = useState(0);
  const [creating, setCreating] = useState(false);
  const [progress, setProgress] = useState({ total: 0, done: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [dimensionWarnings, setDimensionWarnings] = useState([]);

  const BANNER_MIN_WIDTH = 1200;
  const BANNER_MIN_HEIGHT = 300;

  const notify = useCallback((message, severity = 'info') => {
    if (showSnackbar) {
      try { showSnackbar(message, severity); return; } catch (_) {}
    }
    setSnackbar({ open: true, message, severity });
  }, [showSnackbar]);

  const withTimeout = (promise, ms = 30000) => {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Request timed out')), ms);
      promise
        .then((v) => { clearTimeout(timer); resolve(v); })
        .catch((e) => { clearTimeout(timer); reject(e); });
    });
  };

  const removeFile = useCallback((index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const loadAds = useCallback(async () => {
    try {
      const res = await ApiService.adminGetAds();
      const apiOrigin = (ApiService.baseURL || '').replace(/\/api$/, '');
      const resolveMedia = (u) => {
        if (!u) return '';
        try { return u.startsWith('/') ? (apiOrigin + u) : u; } catch { return u; }
      };
      const payload = (res.ads || []).map(a => ({
        ...a,
        mediaUrl: resolveMedia(a.mediaUrl || a.imageUrl || ''),
        imageUrl: resolveMedia(a.imageUrl || a.mediaUrl || ''),
      }));
      setAds(payload);
    } catch (err) {
      notify(err.message || 'Failed to load ads', 'error');
    }
  }, [notify]);

  useEffect(() => { loadAds(); }, [loadAds]);

  const validateImageDimensions = (file) => new Promise((resolve) => {
    try {
      if (!file || !file.type?.startsWith('image/')) return resolve(null);
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (w < BANNER_MIN_WIDTH || h < BANNER_MIN_HEIGHT) {
          resolve(`${file.name}: ${w}x${h}px (min ${BANNER_MIN_WIDTH}x${BANNER_MIN_HEIGHT})`);
        } else {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = URL.createObjectURL(file);
    } catch {
      resolve(null);
    }
  });

  const handleCreate = async () => {
    try {
      if ((!files || files.length === 0) && !mediaUrl.trim()) return notify('Please select media or provide a media URL', 'warning');
      setCreating(true);
      setProgress({ total: files.length, done: 0 });

      // Validate dimensions for images
      const warnings = (await Promise.all(files.map(validateImageDimensions))).filter(Boolean);
      setDimensionWarnings(warnings);

      const results = [];
      for (let i = 0; i < (files?.length || 0); i++) {
        const f = files[i];
        try {
          await withTimeout(ApiService.adminCreateAd({ file: f, title, linkUrl, active, order }), 45000);
          results.push({ file: f.name, ok: true });
        } catch (err) {
          results.push({ file: f.name, ok: false, err: err?.message || 'Failed' });
        } finally {
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        }
      }
      if (mediaUrl.trim()) {
        try {
          await withTimeout(ApiService.adminCreateAd({ mediaUrl: mediaUrl.trim(), title, linkUrl, active, order }), 20000);
          results.push({ file: mediaUrl.trim(), ok: true });
        } catch (err) {
          results.push({ file: mediaUrl.trim(), ok: false, err: err?.message || 'Failed' });
        } finally {
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        }
      }

      const successCount = results.filter(r => r.ok).length;
      const failCount = results.length - successCount;
      if (successCount > 0) notify(`Created ${successCount} banner(s)${failCount ? `, ${failCount} failed` : ''}`, failCount ? 'warning' : 'success');
      if (failCount > 0) notify(results.filter(r => !r.ok).map(r => `${r.file}: ${r.err}`).join('\n'), 'error');

      setFiles([]); setTitle(''); setLinkUrl(''); setMediaUrl(''); setActive(true); setOrder(0);
      await loadAds();
    } catch (err) {
      notify(err?.message || 'Failed to create ad', 'error');
    } finally {
      setCreating(false);
      setProgress({ total: 0, done: 0 });
    }
  };

  const handleToggleActive = async (id, current) => {
    try {
      await ApiService.adminUpdateAd(id, { active: !current });
      loadAds();
    } catch (err) {
      notify(err.message || 'Failed to update ad', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this ad?')) return;
    try {
      await ApiService.adminDeleteAd(id);
      showSnackbar && showSnackbar('Ad deleted', 'success');
      loadAds();
    } catch (err) {
      notify(err.message || 'Failed to delete ad', 'error');
    }
  };

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>Advertising Banners</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload banner images and an optional link. Active banners appear in the home page carousel.
      </Typography>

      <Card sx={{ p: 2, mb: 4 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Button variant="contained" component="label" fullWidth>
                Select Media
                <input hidden type="file" multiple accept="image/*,video/*" onChange={e => setFiles(Array.from(e.target.files || []))} />
              </Button>
              {files && files.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ display: 'block' }}>{files.length} file(s) selected</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, overflowX: 'auto' }}>
                    {files.map((f, idx) => (
                      <Box key={`${f.name}-${idx}`} sx={{ position: 'relative', minWidth: 120 }}>
                        {f.type?.startsWith('video/') ? (
                          <video src={URL.createObjectURL(f)} style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 1 }} muted loop />
                        ) : (
                          <img src={URL.createObjectURL(f)} alt="preview" style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 4 }} />
                        )}
                        <IconButton size="small" onClick={() => removeFile(idx)} sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.9)' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Title" value={title} onChange={e => setTitle(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Link URL (optional)" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Media URL (optional)" value={mediaUrl} onChange={e => setMediaUrl(e.target.value)} placeholder="https://..." />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={<Switch checked={active} onChange={e => setActive(e.target.checked)} />} label="Active" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Order" type="number" value={order} onChange={e => setOrder(parseInt(e.target.value) || 0)} />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">Recommended banner size: {BANNER_MIN_WIDTH}×{BANNER_MIN_HEIGHT} px or larger. Use a ~4:1 aspect ratio for best results.</Alert>
            </Grid>
            {dimensionWarnings.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning">{dimensionWarnings.join(' | ')}</Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <Button variant="contained" color="primary" fullWidth onClick={handleCreate} disabled={creating}>
                {creating ? `Creating… (${progress.done}/${progress.total})` : 'Create Banner'}
              </Button>
            </Grid>
            {creating && (
              <Grid item xs={12}>
                <LinearProgress />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {ads.map(ad => (
          <Grid item xs={12} sm={6} md={4} key={ad._id}>
            <Card>
              {ad.resourceType === 'video' || ad.mediaType === 'video' ? (
                <CardMedia component="video" height="160" src={ad.mediaUrl || ad.imageUrl} autoPlay muted loop />
              ) : (
                <CardMedia component="img" height="160" image={ad.mediaUrl || ad.imageUrl} alt={ad.title || 'Ad'} />
              )}
              <CardContent>
                <Typography variant="subtitle1">{ad.title || 'Untitled'}</Typography>
                <Typography variant="body2" color="text.secondary">{ad.linkUrl || '—'}</Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => handleToggleActive(ad._id, ad.active)}>
                    {ad.active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button size="small" color="error" variant="outlined" onClick={() => handleDelete(ad._id)}>Delete</Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}